import React from 'react';
import { ShieldAlert, ChevronRight, Globe, Server, Database, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/Badge';

interface TopVulnerableAssetsProps {
  assets: any[];
}

const assetTypes: any = {
  domain: "domains",
  subdomain: "subdomains",
  database: "databases",
  ipAddress: "ip-addresses",
  ip_address: "ip-addresses",
  port: "ports",
};

const TopVulnerableAssets: React.FC<TopVulnerableAssetsProps> = ({ assets }) => {
  const navigate = useNavigate();
  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'domain': return Globe;
      case 'subdomain': return Server;
      case 'database': return Database;
      default: return Globe;
    }
  };

  const getRiskColor = (score: string) => {
    switch (score) {
      case "A+": return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      case "A": return 'text-green-500 bg-green-500/10 border-green-500/20';
      case "B": return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      case "C": return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case "F": return 'text-red-500 bg-red-500/10 border-red-500/20';
      default: return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
    }
  };

  return (
    <div className="bg-[#0F1629] rounded-xl p-6 relative overflow-hidden group">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <ShieldAlert className="h-5 w-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-white">Top Vulnerable Assets</h2>
          </div>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <span className="text-sm text-orange-500">High risk detected</span>
          </div>
        </div>

        {/* Assets List */}
        <div className="space-y-3 mb-4">
          {assets.map((asset) => {
            const AssetIcon = getAssetIcon(asset.type);
            return (
              <div
                key={asset.id}
                className="bg-gray-800/20 rounded-lg p-4 hover:bg-gray-800/30 transition-all duration-200 group/item cursor-pointer"
                onClick={() => navigate(`/${assetTypes[asset.type]}/${asset.id}`)}
              >
                <div className="flex items-start space-x-4">
                  <div className="p-2 rounded-lg bg-gray-800/30">
                    <AssetIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-medium text-white truncate">{asset.name}</h3>
                      <Badge className={`text-sm font-bold ${getRiskColor(asset.riskScore.toUpperCase())}`}>
                        {asset.riskScore.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      <div className="text-center p-1 rounded bg-gray-800/30">
                        <div className="text-xs font-medium text-red-500">{asset.findings_statistics.critical}</div>
                        <div className="text-xs text-gray-500">CRITICAL</div>
                      </div>
                      <div className="text-center p-1 rounded bg-gray-800/30">
                        <div className="text-xs font-medium text-orange-500">{asset.findings_statistics.high}</div>
                        <div className="text-xs text-gray-500">HIGH</div>
                      </div>
                      <div className="text-center p-1 rounded bg-gray-800/30">
                        <div className="text-xs font-medium text-yellow-500">{asset.findings_statistics.medium}</div>
                        <div className="text-xs text-gray-500">MEDIUM</div>
                      </div>
                      <div className="text-center p-1 rounded bg-gray-800/30">
                        <div className="text-xs font-medium text-emerald-500">{asset.findings_statistics.low}</div>
                        <div className="text-xs text-gray-500">LOW</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>Last scan: 2 days ago</span>
                      <ChevronRight className="h-4 w-4 text-gray-500 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <button
          className="w-full bg-gray-800/30 hover:bg-gray-800/50 text-gray-400 hover:text-white py-2 rounded-lg transition-colors duration-200 text-sm font-medium flex items-center justify-center space-x-2"
          onClick={() => navigate('/domains')}
        >
          <span>View all assets</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default TopVulnerableAssets;